import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import {
  DEFAULT_DISTANCE_SETTINGS,
  DEFAULT_CATEGORY_SETTINGS,
  type DistanceSettings,
  type CategorySettings,
} from '../components/SettingsModal';
import type { LoadingStage, SearchSubStage, RankingSubStage } from '../components/LoadingScreen';
import type { RankedPlace } from '../hooks/useLLM';
import type { AiMode } from '../hooks/useSettings';
import { useWeather } from '../hooks/useWeather';
import { useTasteProfile } from '../hooks/useTasteProfile';
import { useAuth } from '../hooks/useAuth';

// Re-export types consumers need
export type { DistanceSettings, CategorySettings };

type PlanningView = 'input' | 'loading' | 'results';

export interface SearchData {
  location: string;
  timeAvailable: string;
  transport: string;
  vibes: string[];
  customVibe?: string;
  coords?: { lat: number; lng: number };
  timezone?: string;
  country?: string;
}

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------
interface PlanningContextValue {
  // ── Active view ──
  planningView: PlanningView;
  setPlanningView: (v: PlanningView) => void;

  // ── Search data & results ──
  searchData: SearchData | null;
  setSearchData: (d: SearchData | null) => void;
  rankedPlaces: RankedPlace[];
  setRankedPlaces: React.Dispatch<React.SetStateAction<RankedPlace[]>>;
  errorMessage: string | null;
  setErrorMessage: (msg: string | null) => void;
  filteredClosedCount: number;
  setFilteredClosedCount: (n: number) => void;

  // ── Filter / settings state ──
  distanceSettings: DistanceSettings;
  setDistanceSettings: (d: DistanceSettings) => void;
  categorySettings: CategorySettings;
  setCategorySettings: (c: CategorySettings) => void;
  showOpenOnly: boolean;
  setShowOpenOnly: (v: boolean) => void;
  considerWeather: boolean;
  setConsiderWeather: (v: boolean) => void;
  minRating: number;
  setMinRating: (n: number) => void;
  aiMode: AiMode;
  setAiMode: (m: AiMode) => void;

  // ── Loading sub-states ──
  loadingStage: LoadingStage;
  setLoadingStage: (s: LoadingStage) => void;
  loadingLocationName: string;
  setLoadingLocationName: (n: string) => void;
  loadingPlacesFound: number;
  setLoadingPlacesFound: (n: number) => void;
  loadingPlacesEliminated: number;
  setLoadingPlacesEliminated: (n: number) => void;
  showTopupStage: boolean;
  setShowTopupStage: (v: boolean) => void;
  firstPassAnalyzed: number;
  setFirstPassAnalyzed: (n: number) => void;
  firstPassEliminated: number;
  setFirstPassEliminated: (n: number) => void;
  rankingSubStage: RankingSubStage;
  setRankingSubStage: (s: RankingSubStage) => void;
  searchSubStage: SearchSubStage;
  setSearchSubStage: (s: SearchSubStage) => void;
  searchProgress: { completed: number; total: number };
  setSearchProgress: (p: { completed: number; total: number }) => void;

  // ── Weather ──
  weather: ReturnType<typeof useWeather>['weather'];
  weatherLoading: boolean;
  deviceCoords: { lat: number; lng: number } | null;

  // ── Taste profile defaults ──
  tasteDefaults: { transport?: string; timeAvailable?: string } | undefined;

  // ── Helpers ──
  resetSearchState: () => void;
}

const PlanningContext = createContext<PlanningContextValue | null>(null);

export function usePlanningContext() {
  const ctx = useContext(PlanningContext);
  if (!ctx) throw new Error('usePlanningContext must be used within <PlanningProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function PlanningProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // ── Active view ──
  const [planningView, setPlanningView] = useState<PlanningView>('input');

  // ── Search data & results ──
  const [searchData, setSearchData] = useState<SearchData | null>(null);
  const [rankedPlaces, setRankedPlaces] = useState<RankedPlace[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filteredClosedCount, setFilteredClosedCount] = useState(0);

  // ── Filter / settings state ──
  const [distanceSettings, setDistanceSettings] = useState<DistanceSettings>(DEFAULT_DISTANCE_SETTINGS);
  const [categorySettings, setCategorySettings] = useState<CategorySettings>(DEFAULT_CATEGORY_SETTINGS);
  const [showOpenOnly, setShowOpenOnly] = useState(true);
  const [considerWeather, setConsiderWeather] = useState(true);
  const [minRating, setMinRating] = useState(3.7);
  const [aiMode, setAiMode] = useState<AiMode>('medium');

  // ── Loading sub-states ──
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('validating');
  const [loadingLocationName, setLoadingLocationName] = useState('');
  const [loadingPlacesFound, setLoadingPlacesFound] = useState(0);
  const [loadingPlacesEliminated, setLoadingPlacesEliminated] = useState(0);
  const [showTopupStage, setShowTopupStage] = useState(false);
  const [firstPassAnalyzed, setFirstPassAnalyzed] = useState(0);
  const [firstPassEliminated, setFirstPassEliminated] = useState(0);
  const [rankingSubStage, setRankingSubStage] = useState<RankingSubStage>('sending');
  const [searchSubStage, setSearchSubStage] = useState<SearchSubStage>('vibes');
  const [searchProgress, setSearchProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });

  // ── Quiet device location for weather ──
  const [deviceCoords, setDeviceCoords] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setDeviceCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { timeout: 5000, maximumAge: 300000, enableHighAccuracy: false },
    );
  }, []);

  const weatherCoords = searchData?.coords ?? deviceCoords;
  const { weather, loading: weatherLoading } = useWeather(weatherCoords);

  // ── Taste profile defaults ──
  const { profile: tasteProfile } = useTasteProfile(user?.id ?? null);
  const tasteDefaults = useMemo(() => {
    if (!tasteProfile) return undefined;
    const topOf = (counts: Record<string, number>) => {
      const entries = Object.entries(counts);
      if (entries.length === 0) return undefined;
      return entries.sort((a, b) => b[1] - a[1])[0][0];
    };
    const transport = topOf(tasteProfile.transportCounts || {});
    const timeAvailable = topOf(tasteProfile.timeCounts || {});
    return transport || timeAvailable ? { transport, timeAvailable } : undefined;
  }, [tasteProfile]);

  // ── Helpers ──
  const resetSearchState = () => {
    setPlanningView('input');
    setSearchData(null);
    setRankedPlaces([]);
    setErrorMessage(null);
    setFilteredClosedCount(0);
    setLoadingStage('validating');
    setLoadingLocationName('');
    setLoadingPlacesFound(0);
    setLoadingPlacesEliminated(0);
    setShowTopupStage(false);
    setFirstPassAnalyzed(0);
    setFirstPassEliminated(0);
    setRankingSubStage('sending');
    setSearchSubStage('vibes');
    setSearchProgress({ completed: 0, total: 0 });
  };

  const value: PlanningContextValue = {
    planningView,
    setPlanningView,
    searchData,
    setSearchData,
    rankedPlaces,
    setRankedPlaces,
    errorMessage,
    setErrorMessage,
    filteredClosedCount,
    setFilteredClosedCount,
    distanceSettings,
    setDistanceSettings,
    categorySettings,
    setCategorySettings,
    showOpenOnly,
    setShowOpenOnly,
    considerWeather,
    setConsiderWeather,
    minRating,
    setMinRating,
    aiMode,
    setAiMode,
    loadingStage,
    setLoadingStage,
    loadingLocationName,
    setLoadingLocationName,
    loadingPlacesFound,
    setLoadingPlacesFound,
    loadingPlacesEliminated,
    setLoadingPlacesEliminated,
    showTopupStage,
    setShowTopupStage,
    firstPassAnalyzed,
    setFirstPassAnalyzed,
    firstPassEliminated,
    setFirstPassEliminated,
    rankingSubStage,
    setRankingSubStage,
    searchSubStage,
    setSearchSubStage,
    searchProgress,
    setSearchProgress,
    weather,
    weatherLoading,
    deviceCoords,
    tasteDefaults,
    resetSearchState,
  };

  return <PlanningContext.Provider value={value}>{children}</PlanningContext.Provider>;
}
